// Day/night for the map, from the company-local clock. Cosmetic only: a
// smooth darkness curve around fixed approximate sunrise/sunset times for
// Austin (CDT), not an astronomical model.
import { localMinuteOfDay, localParts } from '../../convex/shared/time';

const SUNRISE_MIN = 7 * 60 + 25;
const SUNSET_MIN = 19 * 60 + 10;
const TWILIGHT_MIN = 45;

const smoothstep = (e0: number, e1: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

/** 0 = full day, 1 = full night, for minutes since local midnight. */
export function darknessAtMinute(minute: number): number {
  const m = ((minute % 1440) + 1440) % 1440;
  const dawn = smoothstep(SUNRISE_MIN - TWILIGHT_MIN, SUNRISE_MIN + TWILIGHT_MIN, m);
  const dusk = smoothstep(SUNSET_MIN - TWILIGHT_MIN, SUNSET_MIN + TWILIGHT_MIN, m);
  return 1 - dawn + dusk;
}

export function darknessAt(ms: number): number {
  return darknessAtMinute(localMinuteOfDay(ms));
}

export type DayPhase = 'night' | 'dawn' | 'day' | 'dusk';

export function dayPhase(ms: number): DayPhase {
  const d = darknessAt(ms);
  if (d > 0.85) return 'night';
  if (d < 0.15) return 'day';
  return localParts(ms).hour < 12 ? 'dawn' : 'dusk';
}

/** NPC traffic density 0..1 by local hour (rush hours busiest, small hours nearly empty). */
export function trafficDensity(ms: number): number {
  const { hour, dow } = localParts(ms);
  const weekend = dow === 0 || dow === 6;
  if (hour < 5) return 0.12;
  if (hour < 7) return weekend ? 0.2 : 0.45;
  if (hour < 9) return weekend ? 0.4 : 1;
  if (hour < 16) return 0.65;
  if (hour < 19) return weekend ? 0.6 : 1;
  if (hour < 22) return 0.5;
  return 0.25;
}
